import {
  RunningUpload,
  RunningUploadsRecord,
  UploadSequence,
} from 'types/RunningUpload';
import { RunningUploadStatus } from 'constants/runningUpload';
import { v4 } from 'uuid';
import { MediaApi } from 'api/media';
import {
  documentToMediaLibraryItem,
  imageToMediaLibraryItem,
  unprocessedVideoToMediaLibraryItem,
} from 'utils/mediaLibrary';
import Logger from 'utils/logger';
import axios from 'axios';
import { ApiClientError } from 'api/client';
import { CreateRunningUploadsReturn } from 'app/utils/runningUploads';

async function createRunningUpload(
  id: string,
  type: 'image' | 'video' | 'document',
  file: File,
): Promise<RunningUpload> {
  function getPreview() {
    if (type === 'video' || type === 'document') {
      return undefined;
    }

    return URL.createObjectURL(file);
  }

  return {
    id,
    base64Preview: await getPreview(),
    file,
    type,
    status: RunningUploadStatus.Pending,
    progress: undefined,
    cancelHandler: axios.CancelToken.source(),
  };
}

type CreateRunningUploadJobsParams = {
  files: { videos: File[]; images: File[]; documents: File[] };
  pageId: number;
  albumId?: number;
  userAccountPublicId: string;
  userId: number;
  uploadSequence: UploadSequence;
};

export async function createRunningUploadsFromFiles({
  files,
  albumId,
  pageId,
  userAccountPublicId,
  userId,
  uploadSequence,
}: CreateRunningUploadJobsParams): Promise<CreateRunningUploadsReturn> {
  const runningUploads: RunningUploadsRecord = {};

  for (const image of files.images) {
    const id = v4();
    runningUploads[id] = await createRunningUpload(id, 'image', image);
  }

  for (const video of files.videos) {
    const id = v4();
    runningUploads[id] = await createRunningUpload(id, 'video', video);
  }

  for (const document of files.documents) {
    const id = v4();
    runningUploads[id] = await createRunningUpload(id, 'document', document);
  }

  const jobs = Object.values(runningUploads).map(
    (runningUpload) => async () => {
      runningUpload.status = RunningUploadStatus.Uploading;

      try {
        if (runningUpload.type === 'image') {
          const response = await MediaApi.uploadImage({
            file: runningUpload.file,
            albumId,
            cancelToken: runningUpload.cancelHandler.token,
            onProgress: (progress: number) =>
              (runningUpload.progress = progress),
          });

          const uploadedImage = Object.values(response)[0];

          runningUpload.result = imageToMediaLibraryItem(uploadedImage);
        }

        if (runningUpload.type === 'video') {
          runningUpload.progress = 0;

          const res = await MediaApi.uploadVideo({
            file: runningUpload.file,
            cancelToken: runningUpload.cancelHandler.token,
            albumId,
            pageId,
            userAccountPublicId,
            userId,
            onProgress: (progress: number) =>
              (runningUpload.progress = progress),
          });

          runningUpload.result = unprocessedVideoToMediaLibraryItem(
            res.videoId,
          );
        }

        if (runningUpload.type === 'document') {
          runningUpload.progress = 0;

          const asset = await MediaApi.uploadAsset({
            file: runningUpload.file,
            cancelToken: runningUpload.cancelHandler.token,
            onProgress: (progress: number) =>
              (runningUpload.progress = progress),
          });

          runningUpload.result = documentToMediaLibraryItem(asset);
        }

        runningUpload.status = RunningUploadStatus.Done;
      } catch (e) {
        Logger.error(e);

        runningUpload.status = RunningUploadStatus.Failed;
        runningUpload.error =
          (e instanceof ApiClientError && e?.userMessage) ||
          'Unable to upload. Please see the requirements and recommended sizes of files below.';
      }

      return runningUpload;
    },
  );

  return {
    jobs: uploadSequence === 'lifo' ? jobs.reverse() : jobs,
    runningUploads,
  };
}
